import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { neo4jService } from '../config/neo4j';
import { userService } from '../services/UserService';
import { matchingService } from '../services/MatchingService';
import { messageService } from '../services/MessageService';
import { transactionService } from '../services/TransactionService';

interface Neo4jContextType {
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  userService: typeof userService;
  matchingService: typeof matchingService;
  messageService: typeof messageService;
  transactionService: typeof transactionService;
  reconnect: () => Promise<void>;
  healthCheck: () => Promise<boolean>;
}

const Neo4jContext = createContext<Neo4jContextType | undefined>(undefined);

export const useNeo4j = () => {
  const context = useContext(Neo4jContext);
  if (!context) {
    throw new Error('useNeo4j must be used within a Neo4jProvider');
  }
  return context;
};

interface Neo4jProviderProps {
  children: ReactNode;
}

export const Neo4jProvider: React.FC<Neo4jProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const connectToNeo4j = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🔌 Connecting to Neo4j...');
      await neo4jService.connect();
      
      // Test the connection
      const isHealthy = await neo4jService.healthCheck();
      if (isHealthy) {
        setIsConnected(true);
        console.log('✅ Neo4j connection established successfully');
      } else {
        throw new Error('Neo4j health check failed');
      }
    } catch (err: any) {
      console.error('❌ Failed to connect to Neo4j:', err);
      setError(err.message || 'Failed to connect to Neo4j');
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const reconnect = async () => {
    await connectToNeo4j();
  };

  const healthCheck = async (): Promise<boolean> => {
    try {
      return await neo4jService.healthCheck();
    } catch (error) {
      console.error('Neo4j health check failed:', error);
      return false;
    }
  };

  useEffect(() => {
    connectToNeo4j();

    // Cleanup on unmount
    return () => {
      neo4jService.disconnect().catch(console.error);
    };
  }, []);

  // Periodic health check
  useEffect(() => {
    if (!isConnected) return;

    const healthCheckInterval = setInterval(async () => {
      const isHealthy = await healthCheck();
      if (!isHealthy && isConnected) {
        console.warn('⚠️ Neo4j connection lost, attempting to reconnect...');
        setIsConnected(false);
        await reconnect();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(healthCheckInterval);
  }, [isConnected]);

  return (
    <Neo4jContext.Provider value={{
      isConnected,
      isLoading,
      error,
      userService,
      matchingService,
      messageService,
      transactionService,
      reconnect,
      healthCheck
    }}>
      {children}
    </Neo4jContext.Provider>
  );
};